import { z } from 'zod';

import { MARKET_CONSOLE_PIPELINES } from './marketConsole';

export const WatchlistSymbolSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .regex(/^[A-Z0-9.-]+$/i, 'Symbol must contain letters, digits, dots, or hyphens.')
  .transform((value) => value.toUpperCase());

export const AddMarketHealthWatchlistItemSchema = z.object({
  symbol: WatchlistSymbolSchema,
});

export type AddMarketHealthWatchlistItemInput = z.infer<typeof AddMarketHealthWatchlistItemSchema>;

export interface MarketSparklinePointDto {
  date: string;
  value: number;
  referenceValue: number | null;
}

export interface MarketTrendDto {
  asOfDate: string;
  price: number;
  sma20: number;
  sma50: number;
  priceVsSma20: 'ABOVE' | 'BELOW';
  priceVsSma50: 'ABOVE' | 'BELOW';
  sma50Slope: 'RISING' | 'FALLING_OR_FLAT';
  atr14: number;
  atrDistanceFromSma50: number;
  history: MarketSparklinePointDto[];
}

export interface DistributionSnapshotDto {
  count: number;
  windowTradingDays: 25;
  defensiveMode: boolean;
  dates: string[];
  daysSinceLastDistribution: number | null;
  postFtdStress: {
    distributionDate: string;
    sessionsAfterFtd: number;
  } | null;
}

export type MarketOutlookStateDto =
  | 'CONFIRMED_UPTREND'
  | 'UPTREND_UNDER_PRESSURE'
  | 'MARKET_IN_CORRECTION';

export interface MarketOutlookTransitionDto {
  date: string;
  fromState: MarketOutlookStateDto | null;
  toState: MarketOutlookStateDto;
  reason: string;
}

export interface MarketOutlookDto {
  state: MarketOutlookStateDto;
  outlookChangedDate: string;
  outlookChangeReason: string;
  ftdDate: string | null;
  ftdLow: number | null;
  daysSinceFtd: number | null;
  rallyAttemptDay: number | null;
  rallyAttemptLow: number | null;
  rallyAttemptStartDate: string | null;
  asOfDate: string;
  computationStatus: 'BACKFILL_REQUIRED' | 'BACKFILLED' | 'LIVE' | 'STALE';
  history: MarketOutlookTransitionDto[];
}

export type FollowThroughStateDto =
  | { state: 'NO_ATTEMPT' }
  | {
      state: 'ATTEMPT_IN_PROGRESS';
      attemptDay: number;
      attemptStartDate: string;
      attemptLow: number;
    }
  | {
      state: 'FTD_CONFIRMED';
      confirmedDate: string;
      confirmedOnAttemptDay: number;
      attemptStartDate: string;
      attemptLow: number;
      followThroughDayLow: number;
      daysSinceConfirmation: number;
    }
  | {
      state: 'FTD_INVALIDATED';
      confirmedDate: string;
      invalidatedDate: string;
      confirmedOnAttemptDay: number;
      attemptStartDate: string;
      attemptLow: number;
      followThroughDayLow: number;
      daysSinceInvalidation: number;
    };

export interface RelativePerformanceDto {
  days5: number | null;
  days10: number | null;
  days20: number | null;
}

export interface MarketHealthWatchlistItemDto {
  id: string;
  symbol: string;
  trend: MarketTrendDto | null;
  relativePerformancePct: RelativePerformanceDto;
  dataStatus: 'READY' | 'INSUFFICIENT_HISTORY';
}

export interface MarketInternalMetricDto {
  value: number | null;
  change: number | null;
  status: 'READY' | 'STALE' | 'NOT_AVAILABLE';
  asOfDate: string | null;
  updatedAt: string | null;
  source: string | null;
}

export interface SectorRotationItemDto {
  symbol: string;
  relativePerformancePct: number;
}

export interface MarketInternalsDto {
  stocksAbove50DayPct: MarketInternalMetricDto;
  advanceDeclineLine: MarketInternalMetricDto;
  volatility: MarketInternalMetricDto;
  newHighs: MarketInternalMetricDto;
  newLows: MarketInternalMetricDto;
  putCallRatio: MarketInternalMetricDto;
  nfci: MarketInternalMetricDto;
  nfciCredit: MarketInternalMetricDto;
  hygLqdRatio: MarketInternalMetricDto;
  sectorRotation: {
    leaders: SectorRotationItemDto[];
    laggards: SectorRotationItemDto[];
    status: 'READY' | 'NOT_AVAILABLE';
  };
}

export interface MarketHealthDto {
  benchmark: 'QQQ';
  trend: MarketTrendDto;
  distribution: DistributionSnapshotDto;
  marketOutlook: MarketOutlookDto;
  followThrough: FollowThroughStateDto;
  internals: MarketInternalsDto;
  watchlist: MarketHealthWatchlistItemDto[];
  rallyAttemptConfiguration: {
    recentLowLookbackDays: number;
    declineLookbackDays: number;
    minimumDeclinePct: number;
    minimumReboundFromLowPct: number;
    followThroughMinimumGainPct: number;
    followThroughMinimumDay: number;
  };
  informationalOnly: true;
}

export const MARKET_DATA_PIPELINES = [
  'DAILY_BARS',
  'BREADTH',
  'VIX',
  'FINANCIAL_CONDITIONS',
  'PUT_CALL',
  'CREDIT_RATIO',
  ...MARKET_CONSOLE_PIPELINES,
] as const;
export const MarketDataPipelineSchema = z.enum(MARKET_DATA_PIPELINES);
export type MarketDataPipeline = z.infer<typeof MarketDataPipelineSchema>;

export interface MarketDataPipelineStatusDto {
  pipeline: MarketDataPipeline;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastAttemptStatus: 'SUCCESS' | 'FAILED' | 'NOT_CONFIGURED' | null;
  lastError: string | null;
  run: 'IDLE' | 'QUEUED' | 'RUNNING';
}

export interface MarketDataPipelinesDto {
  pipelines: MarketDataPipelineStatusDto[];
  queueAvailable: boolean;
}
